import { format, formatDistanceToNowStrict, differenceInMinutes, parseISO } from 'date-fns';
import { he as heDateLocale } from 'date-fns/locale';
import { useLangStore } from '@/stores/langStore';
import { LABELS } from '@/i18n/labels';

/**
 * Format a UTC ISO timestamp to a local readable time string, to the SECOND on a 24-hour clock in both languages
 * (operator, 2026-10-31: a message time must be matchable against the Logs page, whose rows are to the second).
 * Example: "14:34:07"
 */
export function formatTime(isoTimestamp: string): string {
  const lang = useLangStore.getState().lang;
  if (lang === 'he') {
    return format(parseISO(isoTimestamp), 'HH:mm:ss', { locale: heDateLocale });
  }
  return format(parseISO(isoTimestamp), 'HH:mm:ss');
}

/**
 * Format a UTC ISO timestamp to a local date-time string, to the second (same reason as {@link formatTime}).
 * Example (en): "Apr 9, 2026 14:34:07"
 * Example (he): "9 באפר׳ 2026 14:34:07"
 */
export function formatDateTime(isoTimestamp: string): string {
  const lang = useLangStore.getState().lang;
  if (lang === 'he') {
    return format(parseISO(isoTimestamp), 'd בMMM yyyy HH:mm:ss', { locale: heDateLocale });
  }
  return format(parseISO(isoTimestamp), 'MMM d, yyyy HH:mm:ss');
}

/**
 * Format a UTC ISO timestamp as a relative time string.
 * Example: "5 minutes ago", "2 hours ago"
 */
export function formatRelative(isoTimestamp: string): string {
  const lang = useLangStore.getState().lang;
  return formatDistanceToNowStrict(parseISO(isoTimestamp), {
    addSuffix: true,
    locale: lang === 'he' ? heDateLocale : undefined,
  });
}

/**
 * Calculate minutes remaining until an SLA deadline.
 * Returns negative values when the deadline has passed (breach).
 */
export function slaMinutesRemaining(deadlineUtc: string): number {
  return differenceInMinutes(parseISO(deadlineUtc), new Date());
}

/**
 * Format SLA countdown as a human-readable localized string.
 * T9.6 (OP-165): replaced BREACHED/remaining with friendly bilingual labels.
 * Examples (en): "12m left", "Overdue by 3m"
 * Examples (he): "נותרו 12 ד׳", "באיחור של 3 ד׳"
 */
export function formatSlaCountdown(deadlineUtc: string): string {
  const lang = useLangStore.getState().lang;
  const remaining = slaMinutesRemaining(deadlineUtc);
  if (remaining > 0) {
    return LABELS.slaRemaining[lang].replace('{n}', String(remaining));
  }
  return LABELS.slaBreached[lang].replace('{n}', String(Math.abs(remaining)));
}
